import React, { useEffect, useRef, useState } from "react";
import { parseRelease, needsRequiredUpdate } from "../utils/appVersion";
import "./AppUpdateNotice.css";

const currentVersion = Number(process.env.REACT_APP_BUILD_VERSION || 0);
const base = process.env.PUBLIC_URL || "";
const checkInterval = 5 * 60 * 1000;

export default function AppUpdateNotice() {
  const [release, setRelease] = useState(null);
  const [dismissedVersion, setDismissedVersion] = useState(null);
  const [failedUpdate, setFailedUpdate] = useState(false);

  const required = release && needsRequiredUpdate(currentVersion, release);
  const dialogRef = useRef(null);
  useEffect(() => {
    if (required && dialogRef.current && !dialogRef.current.open) {
      dialogRef.current.showModal();
    }
  }, [required]);

  useEffect(() => {
    let disposed = false;
    let inFlight = false;
    let lastCheck = 0;
    let controller;
    let registration;

    // Do not blindly reload on controllerchange: protect unfinished forms.
    const check = async (force = false) => {
      if (!currentVersion || disposed || inFlight || document.hidden ||
          (!force && Date.now() - lastCheck < 15000)) return;
      inFlight = true;
      lastCheck = Date.now();
      controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(`${base}/version.json?t=${Date.now()}`, {
          cache: "no-store", signal: controller.signal,
        });
        if (!response.ok) return;
        const next = parseRelease(await response.json());
        if (!next || disposed) return;
        setRelease(next.version > currentVersion ? next : null);
        // An explicit update must actually deliver the requested release.
        const url = new URL(window.location.href);
        const requested = Number(url.searchParams.get("app-update"));
        if (requested > currentVersion) setFailedUpdate(true);
        else if (requested) {
          url.searchParams.delete("app-update");
          window.history.replaceState(window.history.state, "", url.toString());
          setFailedUpdate(false);
        }
      } catch {
        // Offline/timeouts are not evidence that an update is required.
        // Retry on reconnect, resume, or the next interval.
      } finally {
        clearTimeout(timeout);
        inFlight = false;
      }
    };
    const resume = () => {
      if (document.hidden) return;
      check();
      if (registration) registration.update().catch(() => {});
    };
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register(`${base}/sw.js`, { updateViaCache: "none" })
        .then((value) => {
          if (disposed) return;
          registration = value;
          return registration.update();
        }).catch(() => {});
      navigator.serviceWorker.addEventListener("controllerchange", resume);
    }
    check(true);
    const interval = setInterval(resume, checkInterval);
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);
    window.addEventListener("pageshow", resume);
    window.addEventListener("online", resume);
    return () => {
      disposed = true;
      controller?.abort();
      clearInterval(interval);
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("focus", resume);
      window.removeEventListener("pageshow", resume);
      window.removeEventListener("online", resume);
      navigator.serviceWorker?.removeEventListener("controllerchange", resume);
    };
  }, []);

  const Notice = required ? "dialog" : "section";
  if (!release || (!required && dismissedVersion === release.version)) return null;
  const update = () => {
    if (!window.confirm("Update Social Paws now? Finish or save any changes first. This will reload the app.")) return;
    const url = new URL(window.location.href);
    url.searchParams.set("app-update", String(release.version));
    // Cache-busting navigation; retains login, app storage and the current route.
    window.location.replace(url.toString());
  };
  return (
    <div className={`app-update-container${required ? " app-update-required" : ""}`}>
      <Notice ref={dialogRef} onCancel={required ? (event) => event.preventDefault() : undefined} className="app-update-notice" role={required ? "alertdialog" : "status"}
        aria-modal={required || undefined} aria-labelledby="app-update-title" aria-describedby="app-update-description">
        <strong id="app-update-title">{required ? "Please update Social Paws to continue" : "A new version of Social Paws is available"}</strong>
        <p id="app-update-description">{failedUpdate
          ? "The latest version hasn’t loaded yet. Check your connection and try again. If this continues, close and reopen the app."
          : "Update to get the latest improvements. Save any unfinished changes before updating."}</p>
        <div className="app-update-actions">
          <button type="button" onClick={update}>{failedUpdate ? "Retry update" : "Update now"}</button>
          {!required && <button type="button" onClick={() => setDismissedVersion(release.version)}>Later</button>}
        </div>
      </Notice>
    </div>
  );
}
